import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { simulationDiscussionApi } from "@/features/finances/api/simulationDiscussion";

const commentsKey = (simId: string) =>
  ["order-simulations", simId, "comments"] as const;
const attachmentsKey = (simId: string) =>
  ["order-simulations", simId, "attachments"] as const;

export function useSimulationComments(simId: string | null) {
  return useQuery({
    queryKey: commentsKey(simId ?? ""),
    queryFn: () => simulationDiscussionApi.listComments(simId as string),
    enabled: !!simId,
  });
}

export function useAddSimulationComment(simId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ content, mentionIds = [] }: { content: string; mentionIds?: string[] }) =>
      simulationDiscussionApi.addComment(simId, content, mentionIds),
    onSuccess: () => qc.invalidateQueries({ queryKey: commentsKey(simId) }),
  });
}

export function useDeleteSimulationComment(simId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) => simulationDiscussionApi.deleteComment(simId, commentId),
    onSuccess: () => qc.invalidateQueries({ queryKey: commentsKey(simId) }),
  });
}

export function useSimulationAttachments(simId: string | null) {
  return useQuery({
    queryKey: attachmentsKey(simId ?? ""),
    queryFn: () => simulationDiscussionApi.listAttachments(simId as string),
    enabled: !!simId,
  });
}

export function useUploadSimulationAttachment(simId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => simulationDiscussionApi.uploadAttachment(simId, file),
    onSuccess: () => qc.invalidateQueries({ queryKey: attachmentsKey(simId) }),
  });
}

export function useDeleteSimulationAttachment(simId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (attachmentId: string) =>
      simulationDiscussionApi.deleteAttachment(simId, attachmentId),
    onSuccess: () => qc.invalidateQueries({ queryKey: attachmentsKey(simId) }),
  });
}
